import type { InvoiceHeader, InvoiceItem, InvoiceType } from "../api/inventoryInvoicesApi";
import type { Application } from "../types";
import {
  buildConstructionPoHtml,
  buildPoAnnexureHtml,
  DEFAULT_INVOICE_DOCUMENT_LABELS,
  type BeneficiaryAllocation,
  type ConstructionPoHtmlLine,
} from "./constructionPoDocument";
import { downloadPdfFromHtml, htmlDocumentToPdfBlob, warmPoPdfLogoCache } from "./downloadPdfFromHtml";
import { geoByApplicationIdFromApplications } from "./poBeneficiaryGeo";
import {
  activeGstRows,
  documentTitleForInvoiceType,
  billFromFieldsForPdf,
  billToFieldsForPdf,
  formatBillBlock,
  formatBlockForInvoiceType,
  loadInvoiceFormatSettings,
  type InvoiceFormatSettings,
} from "./invoiceFormatSettings";
import { downloadZip, mapWithConcurrency } from "./downloadZip";

export { mapWithConcurrency };

/** Shown at the bottom of invoice PDFs when no custom footer is configured. */
export const DEFAULT_INVOICE_DIGITAL_FOOTER =
  "This is a computer-generated tax invoice and is valid without a physical signature. Amounts and GST are as per the details recorded at the time of generation.";

function invoiceFooterNote(typeBlock: { footerNote: string }): string {
  return typeBlock.footerNote.trim() || DEFAULT_INVOICE_DIGITAL_FOOTER;
}

/** Annexure is for consolidated / multi-beneficiary invoices only — not single individual PDFs. */
export function shouldIncludeInvoiceAnnexure(
  header: InvoiceHeader,
  allocations: BeneficiaryAllocation[],
): boolean {
  if (allocations.length <= 1) return false;
  if (header.selectionMode === "single" || header.beneficiaryCount <= 1) return false;
  return true;
}

function parseBillBlock(text: string): { name: string; address: string; gstin: string } {
  const lines = String(text ?? "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (!lines.length) return { name: "", address: "", gstin: "" };
  const gstLine = lines.find((l) => /^GSTIN:/i.test(l));
  const gstin = gstLine ? gstLine.replace(/^GSTIN:\s*/i, "").trim() : "";
  const rest = lines.filter((l) => l !== gstLine);
  return {
    name: rest[0] ?? "",
    address: rest.slice(1).join("\n"),
    gstin,
  };
}

function aggregateInvoiceItems(items: InvoiceItem[]): Array<{
  key: string;
  lineLabel: string;
  lineUnit: string;
  totalQty: number;
  rate: number;
  hsnCode: string;
}> {
  const map = new Map<
    string,
    { key: string; lineLabel: string; lineUnit: string; totalQty: number; rate: number; hsnCode: string }
  >();
  for (const it of items) {
    const key = `${it.lineLabel}::${it.catalogStandardItemId || it.lineKey}`;
    const cur = map.get(key);
    if (cur) {
      cur.totalQty += it.qty;
    } else {
      map.set(key, {
        key,
        lineLabel: it.lineLabel,
        lineUnit: it.lineUnit,
        totalQty: it.qty,
        rate: it.rate,
        hsnCode: it.hsnCode,
      });
    }
  }
  return [...map.values()].sort((a, b) => a.lineLabel.localeCompare(b.lineLabel));
}

function getInvoiceBeneficiaryAllocations(
  items: InvoiceItem[],
  geoByApplicationId?: Map<
    string,
    { displayApplicationId: string; district: string; mandal: string; village: string }
  >,
): BeneficiaryAllocation[] {
  const map = new Map<
    string,
    {
      beneficiaryName: string;
      applicationId: string;
      district: string;
      mandal: string;
      village: string;
      itemsMap: Map<string, { label: string; qty: number; unit: string }>;
    }
  >();

  for (const it of items) {
    if (!it.applicationId && !it.beneficiaryName) continue;
    const appKey = String(it.applicationId ?? "").trim() || `name::${(it.beneficiaryName ?? "").trim()}`;
    if (!map.has(appKey)) {
      const geo = geoByApplicationId?.get(String(it.applicationId ?? "").trim());
      map.set(appKey, {
        beneficiaryName: (it.beneficiaryName ?? "").trim() || "—",
        applicationId:
          geo?.displayApplicationId ??
          ((it.beneficiaryMobileApplicationId ?? "").trim() || appKey),
        district: geo?.district?.trim() || "—",
        mandal: geo?.mandal?.trim() || "—",
        village: geo?.village?.trim() || "—",
        itemsMap: new Map(),
      });
    }
    const row = map.get(appKey)!;
    const itemKey = it.lineKey;
    if (!row.itemsMap.has(itemKey)) {
      row.itemsMap.set(itemKey, { label: it.lineLabel, qty: 0, unit: it.lineUnit });
    }
    row.itemsMap.get(itemKey)!.qty += it.qty;
  }

  return [...map.values()]
    .map((row) => ({
      beneficiaryName: row.beneficiaryName,
      applicationId: row.applicationId,
      district: row.district,
      mandal: row.mandal,
      village: row.village,
      items: [...row.itemsMap.values()].sort((a, b) => a.label.localeCompare(b.label)),
    }))
    .sort((a, b) => a.beneficiaryName.localeCompare(b.beneficiaryName));
}

function invoiceReferenceText(header: InvoiceHeader): string {
  const parts: string[] = [];
  if (header.sourceMilestoneTitle?.trim()) parts.push(header.sourceMilestoneTitle.trim());
  if (header.notes?.trim()) parts.push(header.notes.trim());
  return parts.join("\n\n") || "—";
}

export function buildInvoicePdfHtml(
  header: InvoiceHeader,
  items: InvoiceItem[],
  options?: {
    applications?: Application[];
    forPdfCapture?: boolean;
    formatSettings?: InvoiceFormatSettings;
  },
): { html: string; annexureHtml: string; downloadBasename: string } {
  const s = options?.formatSettings ?? loadInvoiceFormatSettings();
  const typeBlock = formatBlockForInvoiceType(s, header.invoiceType);
  const billFrom = billFromFieldsForPdf(header, s);
  const billTo = billToFieldsForPdf(header, s);
  const aggregateQty = typeBlock.aggregateQtyDefault;

  const baseLines = aggregateQty
    ? aggregateInvoiceItems(items)
    : items.map((it) => ({
        key: it.id,
        lineLabel: it.lineLabel,
        lineUnit: it.lineUnit,
        totalQty: it.qty,
        rate: it.rate,
        hsnCode: it.hsnCode,
        beneficiaryName: it.beneficiaryName,
      }));

  const htmlLines: ConstructionPoHtmlLine[] = baseLines.map((ln, idx) => {
    const qty = ln.totalQty;
    const rate = ln.rate ?? 0;
    const subDesc =
      "beneficiaryName" in ln && ln.beneficiaryName
        ? `Beneficiary: ${ln.beneficiaryName}`
        : header.partyFirmName;
    return {
      sno: idx + 1,
      description: ln.lineLabel,
      subDescription: subDesc,
      hsn: (ln.hsnCode ?? "").trim(),
      qty,
      unit: ln.lineUnit,
      rate,
      amount: qty * rate,
    };
  });

  const taxableTotal = htmlLines.reduce((sum, x) => sum + x.amount, 0);
  const gstLines = activeGstRows(s, header.invoiceType).map((g) => ({
    label: g.label.trim() || "Tax",
    ratePercent: g.ratePercent,
    amount: taxableTotal * (g.ratePercent / 100),
  }));
  const grandTotal = taxableTotal + gstLines.reduce((sum, x) => sum + x.amount, 0);

  const geoByAppId = options?.applications?.length
    ? geoByApplicationIdFromApplications(options.applications)
    : undefined;
  const allocations = getInvoiceBeneficiaryAllocations(items, geoByAppId);
  const pdfCap = options?.forPdfCapture === true;
  const footer = invoiceFooterNote(typeBlock);
  const includeAnnexure = shouldIncludeInvoiceAnnexure(header, allocations);

  const html = buildConstructionPoHtml({
    documentTitle: documentTitleForInvoiceType(s, header.invoiceType),
    letterheadTagline: s.letterheadTagline,
    logoPrimaryUrl: s.logoPrimaryUrl,
    logoSecondaryUrl: s.logoSecondaryUrl,
    logoPrimaryHeightPx: s.logoPrimaryHeightPx,
    logoSecondaryHeightPx: s.logoSecondaryHeightPx,
    accentColorHex: s.accentColorHex,
    footerNote: footer,
    defaultFooterNote: DEFAULT_INVOICE_DIGITAL_FOOTER,
    showSignatures: false,
    poNumber: header.invoiceNumber,
    poDate: header.invoiceDate || header.createdAt.slice(0, 10),
    buyerName: billTo.name,
    buyerAddress: billTo.address,
    buyerGstin: billTo.gstin,
    vendorName: billFrom.name,
    vendorAddress: billFrom.address,
    vendorGstin: billFrom.gstin,
    shipTo: invoiceReferenceText(header),
    paymentTerms: typeBlock.paymentTerms,
    lines: htmlLines,
    taxableTotal,
    gstLines,
    grandTotal,
    forPdfCapture: pdfCap,
    labels: DEFAULT_INVOICE_DOCUMENT_LABELS,
  });

  const annexureHtml =
    includeAnnexure
      ? buildPoAnnexureHtml({
          allocations,
          footerNote: footer,
          accentColorHex: s.accentColorHex,
          forPdfCapture: pdfCap,
        })
      : "";

  const safe = header.invoiceNumber.replace(/[^\w.-]+/g, "_");
  return { html, annexureHtml, downloadBasename: `Invoice_${safe}` };
}

/** PDF filename: `{ApplicationId}_{BeneficiaryName}.pdf` */
export function individualInvoicePdfFilename(
  header: InvoiceHeader,
  items: InvoiceItem[],
  applications?: Application[],
  usedNames?: Set<string>,
): string {
  const appById = applications?.length ? new Map(applications.map((a) => [a.id, a])) : undefined;
  const firstItem = items[0];
  const appId =
    firstItem?.beneficiaryMobileApplicationId?.trim() ||
    (firstItem?.applicationId ? appById?.get(firstItem.applicationId)?.mobile.applicationId : "") ||
    header.invoiceNumber;
  const beneficiaryName =
    firstItem?.beneficiaryName?.trim() ||
    (firstItem?.applicationId ? appById?.get(firstItem.applicationId)?.mobile.beneficiaryName : "") ||
    "Beneficiary";
  return uniquePdfFilename(sanitizeInvoicePdfBasename(appId, beneficiaryName), usedNames);
}

function sanitizeInvoicePdfBasename(applicationId: string, beneficiaryName: string): string {
  const app =
    applicationId
      .trim()
      .replace(/[^\w.-]+/g, "_")
      .replace(/^_+|_+$/g, "") || "APP";
  const name =
    beneficiaryName
      .trim()
      .replace(/[^\w\s.-]+/g, "")
      .replace(/\s+/g, "_")
      .replace(/^_+|_+$/g, "")
      .slice(0, 100) || "Beneficiary";
  return `${app}_${name}`;
}

function uniquePdfFilename(base: string, usedNames = new Set<string>()): string {
  let candidate = `${base.replace(/\.pdf$/i, "")}.pdf`;
  let n = 2;
  while (usedNames.has(candidate.toLowerCase())) {
    const stem = base.replace(/\.pdf$/i, "");
    candidate = `${stem}_${n}.pdf`;
    n += 1;
  }
  usedNames.add(candidate.toLowerCase());
  return candidate;
}

export async function invoicePdfBlob(
  header: InvoiceHeader,
  items: InvoiceItem[],
  options?: { applications?: Application[]; skipAnnexure?: boolean; skipLogoWarm?: boolean },
): Promise<Blob> {
  if (!items.length) throw new Error("No invoice lines to export.");
  const s = loadInvoiceFormatSettings();
  if (!options?.skipLogoWarm) {
    await warmPoPdfLogoCache([s.logoPrimaryUrl, s.logoSecondaryUrl]);
  }
  const geoByAppId = options?.applications?.length
    ? geoByApplicationIdFromApplications(options.applications)
    : undefined;
  const allocations = getInvoiceBeneficiaryAllocations(items, geoByAppId);
  const typeBlock = formatBlockForInvoiceType(s, header.invoiceType);
  const footer = invoiceFooterNote(typeBlock);
  const { html } = buildInvoicePdfHtml(header, items, {
    applications: options?.applications,
    forPdfCapture: true,
  });
  const useAnnexure =
    !options?.skipAnnexure && shouldIncludeInvoiceAnnexure(header, allocations);
  return htmlDocumentToPdfBlob(
    html,
    useAnnexure
      ? {
          annexure: {
            allocations,
            accentColorHex: s.accentColorHex,
            footerNote: footer,
          },
        }
      : undefined,
  );
}

export type InvoicePdfBundle = { header: InvoiceHeader; items: InvoiceItem[] };

function normalizeInvoicePdfBundles(
  invoices: Array<{ header?: InvoiceHeader; invoice?: InvoiceHeader; items: InvoiceItem[] }>,
): InvoicePdfBundle[] {
  return invoices.map((entry, index) => {
    const header = entry.header ?? entry.invoice;
    if (!header) throw new Error(`Missing invoice header for PDF export (row ${index + 1}).`);
    if (!entry.items?.length) throw new Error(`Missing invoice lines for PDF export (row ${index + 1}).`);
    return { header, items: entry.items };
  });
}

export async function downloadIndividualInvoicesZip(
  invoices: Array<{ header?: InvoiceHeader; invoice?: InvoiceHeader; items: InvoiceItem[] }>,
  options?: {
    applications?: Application[];
    zipBasename?: string;
    onProgress?: (done: number, total: number) => void;
    pdfConcurrency?: number;
  },
): Promise<void> {
  if (!invoices.length) throw new Error("No invoices to export.");
  const bundles = normalizeInvoicePdfBundles(invoices);
  const s = loadInvoiceFormatSettings();
  await warmPoPdfLogoCache([s.logoPrimaryUrl, s.logoSecondaryUrl]);
  const usedNames = new Set<string>();
  const total = bundles.length;
  const pdfConcurrency = options?.pdfConcurrency ?? 6;
  let pdfDone = 0;

  const files = await mapWithConcurrency(bundles, pdfConcurrency, async ({ header, items }) => {
    const blob = await invoicePdfBlob(header, items, {
      applications: options?.applications,
      skipAnnexure: true,
      skipLogoWarm: true,
    });
    pdfDone += 1;
    options?.onProgress?.(pdfDone, total);
    return {
      path: individualInvoicePdfFilename(header, items, options?.applications, usedNames),
      blob,
    };
  });

  options?.onProgress?.(total, total);
  const zipName =
    options?.zipBasename?.trim() ||
    `Invoices_${bundles.length}_${new Date().toISOString().slice(0, 10)}`;
  await downloadZip(files, zipName);
}

export async function downloadInvoicePdf(
  header: InvoiceHeader,
  items: InvoiceItem[],
  options?: { applications?: Application[] },
): Promise<void> {
  if (!items.length) throw new Error("No invoice lines to export.");
  const s = loadInvoiceFormatSettings();
  await warmPoPdfLogoCache([s.logoPrimaryUrl, s.logoSecondaryUrl]);
  const geoByAppId = options?.applications?.length
    ? geoByApplicationIdFromApplications(options.applications)
    : undefined;
  const typeBlock = formatBlockForInvoiceType(s, header.invoiceType);
  const allocations = getInvoiceBeneficiaryAllocations(items, geoByAppId);
  const footer = invoiceFooterNote(typeBlock);
  const { html, downloadBasename } = buildInvoicePdfHtml(header, items, {
    applications: options?.applications,
    forPdfCapture: true,
  });
  const useAnnexure = shouldIncludeInvoiceAnnexure(header, allocations);
  await downloadPdfFromHtml(
    html,
    downloadBasename,
    useAnnexure
      ? {
          annexure: {
            allocations,
            accentColorHex: s.accentColorHex,
            footerNote: footer,
          },
        }
      : undefined,
  );
}

const SAMPLE_NOW = "2026-05-21T10:00:00.000Z";

function sampleInvoiceItems(invoiceType: InvoiceType, settings?: InvoiceFormatSettings): InvoiceItem[] {
  const base = {
    invoiceId: "sample",
    applicationId: "app-sample-1",
    beneficiaryName: "Sample Beneficiary",
    beneficiaryMobileApplicationId: "APP-2026-0001",
    hsnCode: "2523",
    catalogStandardItemId: "cat-1",
    sourcePoId: null,
    sourcePoItemId: null,
    sourceMilestoneTemplateItemId: null,
    createdAt: SAMPLE_NOW,
  };

  if (invoiceType === "subcontractor" && settings) {
    const row =
      settings.subcontractorBillLines.find((r) => r.rate > 0) ?? settings.subcontractorBillLines[0];
    if (row) {
      const qty = row.qty > 0 ? row.qty : 1;
      const rate = row.rate > 0 ? row.rate : 185000;
      return [
        {
          ...base,
          id: "sample-s1",
          lineKey: row.milestoneTemplateItemId,
          lineLabel: row.lineLabel || "Masonry work — Subcontractor RA Bill",
          lineUnit: row.unit || "LS",
          qty,
          rate,
          lineAmount: qty * rate,
          hsnCode: row.hsnCode || "9954",
        },
      ];
    }
  }

  if (invoiceType === "labour" && settings) {
    const priced = settings.labourBillLines.filter((r) => r.rate > 0);
    const rows = (priced.length ? priced : settings.labourBillLines).slice(0, 2);
    if (rows.length) {
      return rows.map((row, idx) => {
        const qty = row.qty > 0 ? row.qty : 1;
        const rate = row.rate > 0 ? row.rate : 850;
        return {
          ...base,
          id: `sample-l${idx + 1}`,
          lineKey: row.milestoneTemplateItemId,
          lineLabel: row.lineLabel || "Labour Charges",
          lineUnit: row.unit || "LS",
          qty,
          rate,
          lineAmount: qty * rate,
          hsnCode: row.hsnCode || "",
        };
      });
    }
  }

  if (invoiceType === "material") {
    return [
      {
        ...base,
        id: "sample-m1",
        lineKey: "cement",
        lineLabel: "OPC 53 Grade Cement",
        lineUnit: "Bag",
        qty: 120,
        rate: 385,
        lineAmount: 46200,
      },
      {
        ...base,
        id: "sample-m2",
        lineKey: "sand",
        lineLabel: "River Sand (fine)",
        lineUnit: "Cum",
        qty: 8,
        rate: 2200,
        lineAmount: 17600,
      },
    ];
  }
  if (invoiceType === "subcontractor") {
    return [
      {
        ...base,
        id: "sample-s1",
        lineKey: "masonry",
        lineLabel: "Masonry work — ground floor",
        lineUnit: "LS",
        qty: 1,
        rate: 185000,
        lineAmount: 185000,
        hsnCode: "9954",
      },
    ];
  }
  return [
    {
      ...base,
      id: "sample-l1",
      lineKey: "labour-1",
      lineLabel: "Skilled mason — daily wages",
      lineUnit: "Day",
      qty: 24,
      rate: 850,
      lineAmount: 20400,
      hsnCode: "",
    },
    {
      ...base,
      id: "sample-l2",
      lineKey: "labour-2",
      lineLabel: "Helper — daily wages",
      lineUnit: "Day",
      qty: 18,
      rate: 550,
      lineAmount: 9900,
      hsnCode: "",
    },
  ];
}

function sampleInvoiceHeader(invoiceType: InvoiceType, settings: InvoiceFormatSettings): InvoiceHeader {
  const b = formatBlockForInvoiceType(settings, invoiceType);
  const billFrom = formatBillBlock(
    b.billFromName || "Sample Supplier Pvt Ltd",
    b.billFromAddress || "Plot 12, Industrial Area\nHyderabad, Telangana — 500032",
    b.billFromGstin || "36AABCS1234A1Z5",
  );
  const billTo = formatBillBlock(
    b.billToName || "Indiramma Indlu Programme",
    b.billToAddress || "Chief Minister's Office\nHyderabad, Telangana",
    b.billToGstin || "36AAAGT1234A1Z9",
  );
  return {
    id: "sample",
    invoiceNumber: `INV-SAMPLE-${invoiceType.slice(0, 3).toUpperCase()}-001`,
    invoiceType,
    invoiceDate: SAMPLE_NOW.slice(0, 10),
    status: "generated",
    partyKind: invoiceType === "labour" ? "labour_supplier" : "subcontractor",
    partyMasterId: "",
    partyFirmName: b.billFromName || "Sample Supplier Pvt Ltd",
    partyGstin: b.billFromGstin || "36AABCS1234A1Z5",
    partyAddress: b.billFromAddress,
    billFromText: billFrom,
    billToText: billTo,
    beneficiaryCount: 1,
    selectionMode: "single",
    sourcePoId: null,
    sourceMilestoneTemplateId: null,
    sourceMilestoneTitle: invoiceType === "subcontractor" ? "Milestone: Masonry completion" : null,
    subcontractorMasterId: null,
    notes: invoiceType === "material" ? "Against PO / inward for sample preview." : "",
    taxableTotal: 0,
    grandTotal: 0,
    createdAt: SAMPLE_NOW,
    updatedAt: SAMPLE_NOW,
  };
}

export function buildSampleInvoicePreviewHtml(
  settings: InvoiceFormatSettings,
  invoiceType: InvoiceType,
  options?: { forPdfCapture?: boolean },
): string {
  const header = sampleInvoiceHeader(invoiceType, settings);
  const items = sampleInvoiceItems(invoiceType, settings);
  return buildInvoicePdfHtml(header, items, {
    forPdfCapture: options?.forPdfCapture,
    formatSettings: settings,
  }).html;
}

export function buildSampleInvoicePreviewDocument(
  settings: InvoiceFormatSettings,
  invoiceType: InvoiceType,
): { html: string; allocations: BeneficiaryAllocation[]; accentColorHex: string; footerNote: string } {
  const header = sampleInvoiceHeader(invoiceType, settings);
  const items = sampleInvoiceItems(invoiceType, settings);
  const doc = buildInvoicePdfHtml(header, items, { formatSettings: settings });
  const allocations = getInvoiceBeneficiaryAllocations(items);
  const typeBlock = formatBlockForInvoiceType(settings, invoiceType);
  return {
    html: doc.html,
    allocations,
    accentColorHex: settings.accentColorHex,
    footerNote: invoiceFooterNote(typeBlock),
  };
}
